import type {
  PublisherBillingCampaign,
  PublisherBillingMonth,
  PublisherBillingStation,
  PublisherBillingStationMonth,
} from '../../../types/selfService';

// Pure grouping and formatting for the publisher payout ledger (CTV-776,
// columns and the station-month status reworked by CTV-780, the advertiser
// heading / Sales rep column / blended totals / page-shaped CSV by CTV-786).
// Its own module so PayoutLedger.tsx exports components only.

/** "August 2026" from 'YYYY-MM', parsed by hand so no timezone shifts it. */
export const monthLabel = (month: string): string => {
  const m = /^(\d{4})-(\d{2})/.exec(month);
  if (!m) return month;
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
};

/** "Aug 18, 2026" from 'YYYY-MM-DD' (or a timestamp), no timezone shift. */
export const dayLabel = (value: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[Number(m[2]) - 1] ?? m[2]} ${Number(m[3])}, ${m[1]}`;
};

export interface LedgerRow {
  campaign: PublisherBillingCampaign;
  month: PublisherBillingMonth;
  station: PublisherBillingStation;
}

/** The numeric columns of the ledger, summed over a set of rows (CTV-780). */
export interface LedgerTotals {
  delivered: number;
  /**
   * The displayed Invoiced impressions (CTV-780 phase 2): delivered per month
   * with the final month trued up to the goal incl. bonus, or the month's
   * budgeted impressions on a contained-period monthly plan. The bonus is
   * inside it; `bonus` says how much.
   */
  invoiced: number;
  bonus: number;
  grossRevenue: number;
  netRevenue: number;
  waymark: number;
  adServing: number;
  fee: number;
  payout: number;
  commission: number;
}

export const emptyLedgerTotals = (): LedgerTotals => ({
  delivered: 0,
  invoiced: 0,
  bonus: 0,
  grossRevenue: 0,
  netRevenue: 0,
  waymark: 0,
  adServing: 0,
  fee: 0,
  payout: 0,
  commission: 0,
});

export const sumLedgerRows = (rows: LedgerRow[]): LedgerTotals =>
  rows.reduce(
    (acc, r) => ({
      delivered: acc.delivered + r.month.delivered_impressions,
      invoiced: acc.invoiced + r.month.invoiced_impressions,
      bonus: acc.bonus + r.month.bonus_impressions,
      grossRevenue: acc.grossRevenue + r.month.gross_revenue_cents,
      netRevenue: acc.netRevenue + r.month.net_revenue_cents,
      waymark: acc.waymark + r.month.waymark_deduction_cents,
      adServing: acc.adServing + r.month.ad_serving_deduction_cents,
      fee: acc.fee + r.month.stripe_fee_cents,
      payout: acc.payout + r.month.net_payout_cents,
      commission: acc.commission + r.month.commission_cents,
    }),
    emptyLedgerTotals(),
  );

export interface MonthGroup {
  month: string;
  rows: LedgerRow[];
  totals: LedgerTotals;
}

/**
 * The station-month payout batch for a row's station and month (CTV-780 /
 * ADR-0044): the ONE payment status the page shows, read off the server
 * rollup so the ledger and the CSV cannot disagree.
 */
export const stationMonthFor = (
  station: PublisherBillingStation,
  month: string,
): PublisherBillingStationMonth | null =>
  (station.months ?? []).find((m) => m.month === month) ?? null;

/** Group every campaign-month across the given stations by delivery month, newest first. */
export function groupByMonth(stations: PublisherBillingStation[]): MonthGroup[] {
  const groups = new Map<string, LedgerRow[]>();
  for (const station of stations) {
    for (const campaign of station.campaigns) {
      for (const month of campaign.months) {
        const list = groups.get(month.month) ?? [];
        list.push({ campaign, month, station });
        groups.set(month.month, list);
      }
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([month, rows]) => ({
      month,
      rows: rows.sort(
        (a, b) =>
          b.month.net_revenue_cents - a.month.net_revenue_cents ||
          a.campaign.campaign_name.localeCompare(b.campaign.campaign_name),
      ),
      totals: sumLedgerRows(rows),
    }));
}

/**
 * A CPM in dollars, without a pointless ".00" on a whole-dollar rate (CTV-786):
 * "$3", "$1.25", "$2.50". Rounds to cents FIRST, because a rate derived by
 * dividing a rounded deduction by impressions lands on 2.99998 rather than 3.
 */
export const cpmLabel = (dollars: number): string => {
  const cents = Math.round(dollars * 100);
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
};

/** "$18.18" for the eCPM column; null when the campaign has no goal. */
export const ecpmLabel = (campaign: PublisherBillingCampaign): string | null =>
  campaign.effective_cpm_dollars != null ? `$${campaign.effective_cpm_dollars.toFixed(2)}` : null;

/** "20%" / "22.5%": a percent with at most one decimal, none when whole. */
const percentLabel = (pct: number): string =>
  `${Number.isInteger(Math.round(pct * 10) / 10) ? pct.toFixed(0) : pct.toFixed(1)}%`;

/** "20%" / "22.5%": CTVBuyer's revenue share, the Rev share column. */
export const revShareLabel = (campaign: PublisherBillingCampaign): string =>
  percentLabel(campaign.rev_share_percent * 100);

/**
 * The blended eCPM of a set of rows (CTV-786): gross revenue ÷ displayed
 * invoiced impressions × 1,000, for the station subtotal and the all-stations
 * band. Null when nothing was invoiced (nothing to divide by).
 */
export const blendedEcpmLabel = (t: LedgerTotals): string | null =>
  t.invoiced > 0 ? `$${((t.grossRevenue / 100 / t.invoiced) * 1000).toFixed(2)}` : null;

/**
 * The blended rev share of a set of rows (CTV-786): 1 − net revenue ÷ gross
 * revenue, i.e. the share CTVBuyer kept across the rows. Null without gross
 * revenue.
 */
export const blendedRevShareLabel = (t: LedgerTotals): string | null =>
  t.grossRevenue > 0 ? percentLabel((1 - t.netRevenue / t.grossRevenue) * 100) : null;

/**
 * The "N% bonus" subtext under the eCPM figure (CTV-786; CTV-780 had it under
 * Invoiced impressions): the campaign's Plays boost percent, on every row of
 * a bonus campaign, because the eCPM it dilutes is a whole-campaign rate.
 */
export const bonusPercentLabel = (campaign: PublisherBillingCampaign): string | null =>
  campaign.plays_boost_percent > 0 ? `${campaign.plays_boost_percent}% bonus` : null;

/**
 * The Sales rep column (CTV-786): the rep's name and the commission percent
 * frozen on the redemption (CTV-673). The API sends a rep only when a percent
 * was frozen, so a name without one is defensive.
 */
export const salesRepLabel = (campaign: PublisherBillingCampaign): string | null => {
  const rep = campaign.sales_rep;
  if (!rep) return null;
  return rep.commission_percent != null ? `${rep.name} · ${rep.commission_percent}%` : rep.name;
};

/** The columns a reader can sort the ledger by (CTV-786). */
export type LedgerSortKey =
  | 'station'
  | 'advertiser'
  | 'delivered'
  | 'invoiced'
  | 'ecpm'
  | 'gross'
  | 'revShare'
  | 'net'
  | 'waymark'
  | 'adServing'
  | 'fee'
  | 'payout'
  | 'rep';

export interface LedgerSort {
  key: LedgerSortKey;
  dir: 'asc' | 'desc';
}

/** What a row is worth in a given column. Text sorts as text, money as money. */
const sortValue = (row: LedgerRow, key: LedgerSortKey): string | number => {
  const { campaign, month, station } = row;
  switch (key) {
    case 'station':
      return station.display_name || station.name;
    case 'advertiser':
      return campaign.advertiser_name || campaign.campaign_name;
    case 'rep':
      return campaign.sales_rep?.name ?? '';
    case 'delivered':
      return month.delivered_impressions;
    case 'invoiced':
      return month.invoiced_impressions;
    // A campaign with no goal has no eCPM. -1 keeps those together at the
    // bottom of a descending sort rather than scattering them.
    case 'ecpm':
      return campaign.effective_cpm_dollars ?? -1;
    case 'gross':
      return month.gross_revenue_cents;
    case 'revShare':
      return campaign.rev_share_percent;
    case 'net':
      return month.net_revenue_cents;
    case 'waymark':
      return month.waymark_deduction_cents;
    case 'adServing':
      return month.ad_serving_deduction_cents;
    case 'fee':
      return month.stripe_fee_cents;
    case 'payout':
      return month.net_payout_cents;
  }
};

/**
 * The rows in a reader-chosen order (CTV-786). `null` leaves them exactly as
 * the server and `groupByMonth` arranged them (station order, then net revenue
 * descending), so the ledger looks unchanged until a header is clicked.
 *
 * Ties break on campaign name so a sort is stable and repeatable, which
 * matters on the money columns where whole groups of rows sit at $0.00.
 */
export function sortLedgerRows(rows: LedgerRow[], sort: LedgerSort | null): LedgerRow[] {
  if (!sort) return rows;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    const cmp =
      typeof av === 'string' && typeof bv === 'string'
        ? av.localeCompare(bv)
        : Number(av) - Number(bv);
    return cmp !== 0
      ? cmp * factor
      : a.campaign.campaign_name.localeCompare(b.campaign.campaign_name);
  });
}

/** 'YYYY-MM' of today (UTC), parsed by hand so no timezone shifts it. */
export const currentMonthKey = (today: string | Date = new Date()): string => {
  if (typeof today === 'string') return today.slice(0, 7);
  return `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** 'YYYY-MM' shifted by `delta` months (negative = earlier). */
export const shiftMonth = (month: string, delta: number): string => {
  const [y, m] = month.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
};

/**
 * The month the tab opens on: the latest delivery month with a row (a
 * publisher usually wants the most recent settled or due month, not an empty
 * current one), falling back to the current month when nothing has run yet.
 */
export const defaultLedgerMonth = (
  stations: PublisherBillingStation[],
  today: string,
): string => {
  let latest: string | null = null;
  for (const st of stations) {
    for (const c of st.campaigns) {
      for (const m of c.months) {
        if (latest == null || m.month > latest) latest = m.month;
      }
    }
  }
  return latest ?? currentMonthKey(today);
};

// CSV helpers, RFC 4180-ish: quote any field with commas, quotes, or
// newlines. Same shape as the Referrals export so Excel / Sheets open both
// natively.
export const csvCell = (v: string | number | null | undefined): string => {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const dollars = (cents: number) => (cents / 100).toFixed(2);

/**
 * The station-month payout batch's label (CTV-780 / ADR-0044): Paid / Due /
 * Expected / Nothing due, from the server rollup. A month whose batch is
 * missing (a row with no rollup entry, which the server never produces) reads
 * from the row as a last resort.
 */
export const batchStatusLabel = (
  batch: PublisherBillingStationMonth | null,
  fallback?: PublisherBillingMonth,
): string => {
  const status = batch?.display_status ?? fallback?.display_status ?? 'expected';
  if (status === 'paid') return 'Paid';
  if (status === 'due_now') return 'Due';
  if (status === 'nothing_due') return 'Nothing due';
  return 'Expected';
};

/**
 * The batch status as the subtotal row prints it (CTV-786 CSV): "Paid Jun 29,
 * 2026 · tr_abc", "Due · payable since Aug 30, 2026", "Expected Sep 29, 2026",
 * "Nothing due".
 */
export const batchStatusSentence = (batch: PublisherBillingStationMonth | null): string => {
  if (!batch) return '';
  if (batch.display_status === 'paid') {
    const when = batch.settled_at ? ` ${dayLabel(batch.settled_at)}` : '';
    return `Paid${when}${batch.stripe_transfer_id ? ` · ${batch.stripe_transfer_id}` : ''}`;
  }
  if (batch.display_status === 'due_now') return `Due · payable since ${dayLabel(batch.payable_at)}`;
  if (batch.display_status === 'nothing_due') return 'Nothing due';
  return `Expected ${dayLabel(batch.payable_at)}`;
};

/** The CSV's column header (CTV-786): Station first, then the page's columns, then the station-month payout status. */
export const CSV_HEADER = [
  'Station', 'Advertiser', 'Campaign', 'Delivered impressions', 'Invoiced impressions', 'eCPM', 'Bonus %',
  'Gross revenue', 'Rev share %', 'Net revenue', 'Waymark', 'Ad serving', 'Stripe fees', 'Payout',
  'Sales rep', 'Commission %', 'Payout status',
];

const bare = (pct: string | null) => (pct == null ? '' : pct.replace(/[$%]/g, ''));

/**
 * One delivery month as a CSV report (CTV-786, shaped per Allen Media's
 * operations manager): ONE table, a Station column on the far left, one line
 * per campaign in station order, and a single Total line last carrying the
 * sums and the blended eCPM / rev share. Each campaign line carries its
 * station-month payout status (Paid / Due / Expected, with date and transfer
 * id) in the last column. Money is plain dollars, rates plain numbers, so a
 * spreadsheet can sum them. The arithmetic is the CHARGED arithmetic the
 * ledger shows (Net revenue − Waymark − Ad serving − Stripe fees = Payout,
 * floored at zero).
 */
export function buildMonthCsv(month: string, stations: PublisherBillingStation[]): string {
  const lines: string[][] = [];
  const all: LedgerRow[] = [];
  for (const station of stations) {
    const group = groupByMonth([station]).find((g) => g.month === month);
    if (!group) continue;
    const batch = stationMonthFor(station, month);
    const stationName = station.display_name || station.name;
    for (const row of group.rows) {
      const { campaign, month: m } = row;
      const rep = campaign.sales_rep;
      all.push(row);
      lines.push([
        stationName,
        campaign.advertiser_name ?? '',
        campaign.campaign_name,
        String(m.delivered_impressions),
        String(m.invoiced_impressions),
        campaign.effective_cpm_dollars != null ? campaign.effective_cpm_dollars.toFixed(2) : '',
        campaign.plays_boost_percent > 0 ? String(campaign.plays_boost_percent) : '',
        dollars(m.gross_revenue_cents),
        bare(revShareLabel(campaign)),
        dollars(m.net_revenue_cents),
        dollars(m.waymark_deduction_cents),
        dollars(m.ad_serving_deduction_cents),
        dollars(m.stripe_fee_cents),
        dollars(m.net_payout_cents),
        rep?.name ?? '',
        rep?.commission_percent != null ? String(rep.commission_percent) : '',
        campaign.status === 'payment_refunded' ? 'Refunded' : batchStatusSentence(batch),
      ]);
    }
  }
  if (lines.length === 0) return '';
  const t = sumLedgerRows(all);
  lines.push([
    'Total', '', `${all.length} ${all.length === 1 ? 'campaign' : 'campaigns'}`, String(t.delivered), String(t.invoiced),
    bare(blendedEcpmLabel(t)), '', dollars(t.grossRevenue), bare(blendedRevShareLabel(t)), dollars(t.netRevenue),
    dollars(t.waymark), dollars(t.adServing), dollars(t.fee), dollars(t.payout), '', '', '',
  ]);
  return [CSV_HEADER, ...lines].map((r) => r.map(csvCell).join(',')).join('\r\n');
}
